import React, { useEffect } from "react";
import { Box, Text, VStack, Spinner } from "@chakra-ui/react";
import { AppEmbed, useEmbedRef } from "@thoughtspot/visual-embed-sdk/react";
import { EmbedEvent } from "@thoughtspot/visual-embed-sdk";
import { useUser } from "context/UserContext";
import { ColumnInfo, Header, ModelInfo } from "build-ts/metadata";
import { DataSourceType, dataSourceTypeToJSON } from "build-ts/enums";
import { syncDataModel } from "services/DataModelService";
import { useNavigate } from "react-router-dom";

interface ThoughtSpotEmbedProps {
  setModelInfo?: React.Dispatch<React.SetStateAction<ModelInfo | undefined>> | undefined;
  modelList?: Header[] | undefined;
  tsModelId?: string;
  setAddedColumns?: React.Dispatch<React.SetStateAction<ColumnInfo[]>> | undefined;
  setRemovedColumns?: React.Dispatch<React.SetStateAction<ColumnInfo[]>> | undefined;
}

const ThoughtSpotEmbed: React.FC<ThoughtSpotEmbedProps> = ({
  setModelInfo,
  modelList,
  tsModelId,
  setAddedColumns,
  setRemovedColumns,
}) => {
  const navigate = useNavigate();
  const embedRef = useEmbedRef<typeof AppEmbed>();
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const { tsBaseUrl, isTSInitialized } = useUser();

  // this useEffect is to listen for ThoughtSpot post messages
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin === tsBaseUrl) {
        // console.log("ThoughtSpot postMessage:", event.data);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  return (
    <Box width="100%" minHeight="calc(100vh - 100px)" height="100%" bg="white" position="relative">
      {(isLoading || !isTSInitialized) && (
        <VStack position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" spacing={4}>
          <Spinner size="xl" color="purple.500" thickness="4px" />
          <Text>Loading ThoughtSpot...</Text>
        </VStack>
      )}

      {error && (
        <VStack position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" spacing={4}>
          <Text color="red.500">Error loading ThoughtSpot: {error}</Text>
        </VStack>
      )}

      {isTSInitialized && (
        <AppEmbed
          ref={embedRef}
          {...(tsModelId ? { path: `worksheet/${tsModelId}` } : { path: "data/embrace" })}
          showPrimaryNavbar={false} // Hide ThoughtSpot's top navbar
          hideApplicationSwitcher={true} // Hide app switcher
          disableProfileAndHelp={true} // Hide profile menu
          frameParams={{
            width: "100%",
            height: "100%",
          }}
          onLoad={() => {
            setIsLoading(false);
            embedRef.current?.on(EmbedEvent.CreateModel, async (payload: any) => {
              if (payload?.status !== "end") return;

              const guid = payload?.data?.data?.Worksheet__operation?.worksheetHeader?.guid as string;
              if (guid) {
                const data = await syncDataModel(null, dataSourceTypeToJSON(DataSourceType.THOUGHTSPOT), guid, true);
                setAddedColumns?.(data?.columnsAdded || []);
                setRemovedColumns?.(data?.columnsRemoved || []);

                const modelId = data?.modelInfo?.header?.id;
                const isPresent = modelList?.find((model: Header) => model.id === modelId);
                // if data.modelInfo.header.id is not in modelList, then we navigate to (/mkg/models/new)
                if (!isPresent) {
                  navigate("/mkg/models/new");
                  if (data?.modelInfo) {
                    setModelInfo?.(data?.modelInfo);
                  }
                } else {
                  // if data.modelInfo.header.id is in modelList, then we navigate to (/mkg/models/id)
                  navigate(`/mkg/models/${modelId}`);
                }
              }
            });

            embedRef.current?.on(EmbedEvent.Alert, (payload: any) => {
              if (payload?.data?.alertMessage === "Table saved successfully.") {
                // console.log("Table saved", payload);
                // trigger UI after table saves
              }
            });
          }}
          onError={(error: any) => {
            setIsLoading(false);
            setError(error?.message || "Unknown error");
            console.error("ThoughtSpot error:", error);
          }}
          style={{
            width: "100%",
            height: "100%",
          }}
          customizations={{
            style: {
              customCSS: {
                rules_UNSTABLE: {
                  ".footer-module__footerLogo": {
                    display: "none !important", // Hide ThoughtSpot logo
                  },
                },
              },
            },
          }}
        />
      )}
    </Box>
  );
};

export default ThoughtSpotEmbed;
